import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { adminApi, reservationApi } from '../../services/api';
import { 
  FiUsers, 
  FiCalendar, 
  FiBarChart2,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiX,
  FiUserCheck,
  FiUserPlus,
  FiEye 
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format, differenceInMinutes, parseISO } from 'date-fns';

function AdminDashboardPage() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, pending: 0, admins: 0 },
    reservations: { total: 0, active: 0, cancelled: 0 },
    utilization: 0
  });
  const [recentReservations, setRecentReservations] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get statistics
      const statsResponse = await adminApi.getStatistics({
        startDate: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // Last 30 days
        endDate: format(new Date(), 'yyyy-MM-dd')
      });
      
      setStats(statsResponse.data.statistics);
      
      // Get recent reservations
      const reservationsResponse = await adminApi.getAllReservations({
        page: 1,
        limit: 10,
        startDate: format(new Date(), 'yyyy-MM-dd')
      });
      
      setRecentReservations(reservationsResponse.data.reservations || []);
      
      // Get pending users
      const pendingUsersResponse = await adminApi.getPendingUsers();
      setPendingUsers(pendingUsersResponse.data.users || []);
      
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Nepodařilo se načíst statistiky. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání dat');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
    
    // Refresh every minute
    const interval = setInterval(fetchStatistics, 60000);
    
    return () => clearInterval(interval);
  }, [fetchStatistics]);

  // Approve user
  const handleApproveUser = async (userId) => {
    try {
      await adminApi.approveUser(userId);
      toast.success('Uživatel schválen');
      fetchStatistics();
    } catch (err) {
      console.error('Error approving user:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se schválit uživatele';
      toast.error(message);
    }
  };

  // Make admin
  const handleMakeAdmin = async (userId) => {
    try {
      if (userId === user.id) {
        toast.error('Nelze povýšit sebe sama');
        return;
      }
      
      await adminApi.makeAdmin(userId);
      toast.success('Uživatel povýšen na administrátora');
      fetchStatistics();
    } catch (err) {
      console.error('Error making user admin:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se povýšit uživatele';
      toast.error(message);
    }
  };

  // Deactivate user
  const handleDeactivateUser = async (userId) => {
    try {
      if (userId === user.id) {
        toast.error('Nelze deaktivovat sebe sama');
        return;
      }
      
      if (!window.confirm('Opravdu chcete deaktivovat tohoto uživatele? Všechny budoucí rezervace budou zrušeny.')) {
        return;
      }
      
      await adminApi.deactivateUser(userId);
      toast.success('Uživatel deaktivován');
      fetchStatistics();
    } catch (err) {
      console.error('Error deactivating user:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se deaktivovat uživatele';
      toast.error(message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  // Get duration
  const getDuration = (startTime, endTime) => {
    try {
      const duration = differenceInMinutes(new Date(endTime), new Date(startTime));
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      
      if (hours === 0) {
        return `${minutes} min`;
      } else if (minutes === 0) {
        return `${hours} h`;
      } else {
        return `${hours} h ${minutes} min`;
      }
    } catch {
      return 'N/A';
    }
  };

  // Get status color
  const getStatusColor = (isCancelled) => {
    return isCancelled 
      ? 'bg-red-100 text-red-800' 
      : 'bg-green-100 text-green-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Administrátorský panel</h1>
        <p className="text-sm text-gray-600 mt-1">
          Přehled systému a správa
        </p>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Users Stats */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Celkem uživatelů</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.users.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FiUsers className="text-xl text-blue-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between text-sm">
                <div>
                  <div className="text-gray-500">Aktivní</div>
                  <div className="font-medium text-gray-900">{stats.users.active}</div>
                </div>
                <div>
                  <div className="text-gray-500">Čekající</div>
                  <div className="font-medium text-yellow-600">{stats.users.pending}</div>
                </div>
                <div>
                  <div className="text-gray-500">Admini</div>
                  <div className="font-medium text-blue-600">{stats.users.admins}</div>
                </div>
              </div>
            </div>

            {/* Reservations Stats */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Celkem rezervací</h3>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.reservations.total}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FiCalendar className="text-xl text-green-600" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between text-sm">
                <div>
                  <div className="text-gray-500">Aktivní</div>
                  <div className="font-medium text-green-600">{stats.reservations.active}</div>
                </div>
                <div>
                  <div className="text-gray-500">Zrušeno</div>
                  <div className="font-medium text-red-600">{stats.reservations.cancelled}</div>
                </div>
                <div>
                  <div className="text-gray-500">Využití</div>
                  <div className="font-medium text-blue-600">{stats.utilization}%</div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Rychlé akce</h3>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = '/admin/users'}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FiUsers className="text-blue-600 mr-2" />
                    <span>Správa uživatelů</span>
                  </div>
                  <FiEye className="text-gray-400" />
                </button>
                
                <button
                  onClick={() => window.location.href = '/admin/reservations'}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FiCalendar className="text-green-600 mr-2" />
                    <span>Správa rezervací</span>
                  </div>
                  <FiEye className="text-gray-400" />
                </button>
                
                <button
                  onClick={() => window.location.href = '/admin/statistics'}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <FiBarChart2 className="text-purple-600 mr-2" />
                    <span>Statistiky</span>
                  </div>
                  <FiEye className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-4">Stav systému</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <FiCheckCircle className="text-green-600 mr-2" />
                    <span>API server</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Aktivní</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <FiCheckCircle className="text-green-600 mr-2" />
                    <span>Databáze</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Připojeno</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center">
                    <FiClock className="text-yellow-600 mr-2" />
                    <span>MCP Email</span>
                  </div>
                  <span className="text-sm text-yellow-600 font-medium">Čeká na konfiguraci</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Users */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Uživatelé čekající na schválení
                </h2>
                <span className="text-sm text-gray-500">
                  {pendingUsers.length} nových žádostí
                </span>
              </div>
              
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8">
                  <FiUserCheck className="w-12 h-12 text-green-200 mx-auto mb-2" />
                  <p className="text-gray-500">Žádní noví uživatelé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingUsers.map(user => (
                    <div key={user.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </h3>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400">
                            Zaregistrováno: {format(new Date(user.createdAt), 'dd.MM.yyyy HH:mm')}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowUserModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Zobrazit detail"
                          >
                            <FiEye />
                          </button>
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            className="flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <FiCheckCircle className="mr-1" />
                            Schválit
                          </button>
                          <button
                            onClick={() => handleDeactivateUser(user.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Zamítnout"
                          >
                            <FiX />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Reservations */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Nedávné rezervace
                </h2>
                <span className="text-sm text-gray-500">
                  Posledních 10
                </span>
              </div>
              
              {recentReservations.length === 0 ? (
                <div className="text-center py-8">
                  <FiCalendar className="w-12 h-12 text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-500">Žádné nedávné rezervace</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentReservations.map(reservation => (
                    <div 
                      key={reservation.id} 
                      className="p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-gray-900 truncate max-w-xs">
                            {reservation.title}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {reservation.user?.firstName} {reservation.user?.lastName}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(reservation.startTime)} - {getDuration(reservation.startTime, reservation.endTime)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.isCancelled)}`}>
                            {reservation.isCancelled ? 'Zrušeno' : 'Aktivní'}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowReservationModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <FiEye />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* User Detail Modal */}
          {showUserModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Detail uživatele</h2>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX className="text-xl" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">{selectedUser.email}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Zaregistrováno</div>
                      <div className="font-medium text-gray-900">
                        {format(new Date(selectedUser.createdAt), 'dd.MM.yyyy HH:mm')}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Stav</div>
                      <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                        Čeká na schválení
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t border-gray-200 space-x-2">
                    <button
                      onClick={() => {
                        handleApproveUser(selectedUser.id);
                        setShowUserModal(false);
                      }}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700 transition-colors"
                    >
                      <FiCheckCircle className="mr-2" />
                      Schválit
                    </button>
                    <button
                      onClick={() => {
                        handleDeactivateUser(selectedUser.id);
                        setShowUserModal(false);
                      }}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 transition-colors"
                    >
                      <FiX className="mr-2" />
                      Zamítnout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reservation Detail Modal */}
          {showReservationModal && selectedReservation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Detail rezervace</h2>
                  <button
                    onClick={() => setShowReservationModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FiX className="text-xl" />
                  </button>
                </div>
                
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">{selectedReservation.title}</h3>
                    {selectedReservation.description && (
                      <p className="text-gray-600 mt-2">{selectedReservation.description}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Uživatel</div>
                      <div className="font-medium text-gray-900">
                        {selectedReservation.user?.firstName} {selectedReservation.user?.lastName}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="font-medium text-gray-900">
                        {selectedReservation.user?.email}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Začátek</div>
                      <div className="font-medium text-gray-900">
                        {formatDate(selectedReservation.startTime)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Konec</div>
                      <div className="font-medium text-gray-900">
                        {formatDate(selectedReservation.endTime)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Trvání</div>
                      <div className="font-medium text-gray-900">
                        {getDuration(selectedReservation.startTime, selectedReservation.endTime)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Stav</div>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedReservation.isCancelled)}`}>
                        {selectedReservation.isCancelled ? 'Zrušeno' : 'Aktivní'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowReservationModal(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      Zavřít
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminDashboardPage;
